'use client';

import { cn } from '@/shadcn/lib/utils';
import { ArrowRightLongIcon } from '@/shared/components/icons';

type Props = {
    page: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    siblingCount?: number;
};

function getPaginationPages(
    currentPage: number,
    totalPages: number,
    siblingCount = 1,
): Array<number | 'ellipsis'> {
    const pages: Array<number | 'ellipsis'> = [];

    const startPage = Math.max(2, currentPage - siblingCount);
    const endPage = Math.min(totalPages - 1, currentPage + siblingCount);

    pages.push(1);

    if (startPage > 2) pages.push('ellipsis');

    for (let p = startPage; p <= endPage; p++) pages.push(p);

    if (endPage < totalPages - 1) pages.push('ellipsis');

    if (totalPages > 1) pages.push(totalPages);

    return pages;
}

function ScrollToTopIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-[14px] h-[14px]"
        >
            <path d="M12 19V5M5 12l7-7 7 7" />
        </svg>
    );
}

export function Pagination({ page, totalPages, onPageChange, siblingCount = 1 }: Props) {
    if (totalPages <= 1) return null;

    const pages = getPaginationPages(page, totalPages, siblingCount);

    const goToPage = (nextPage: number) => {
        if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
        onPageChange(nextPage);
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="flex items-center justify-between gap-[20px]">
            <div>
                {page != totalPages && (
                    <button
                        disabled={page >= totalPages}
                        onClick={() => goToPage(page + 1)}
                        className={cn(
                            'flex items-center gap-[8px] shrink-0',
                            'bg-black text-white text-[12px] xl:text-[13px] font-[600]',
                            'min-h-[40px] xl:min-h-[45px] rounded-[30px] px-[16px] xl:px-[19px]',
                            'hover:bg-neutral-800 transition-colors duration-200 cursor-pointer',
                            'disabled:opacity-50 disabled:pointer-events-none',
                        )}
                    >
                        Ďalšie
                        <ArrowRightLongIcon className="w-[14px] h-[14px] fill-white" />
                    </button>
                )}
            </div>

            <div className="flex items-center gap-[4px] flex-wrap">
                {pages.map((item, index) =>
                    item === 'ellipsis' ? (
                        <span
                            key={`ellipsis-${index}`}
                            className="w-[30px] text-center text-[13px] text-neutral-500 select-none"
                        >
                            ...
                        </span>
                    ) : (
                        <button
                            key={item}
                            onClick={() => goToPage(item)}
                            className={cn(
                                'min-w-[30px] h-[30px] px-[4px] text-[13px] font-[500] rounded-[4px] flex justify-center items-center',
                                'transition-colors duration-150 cursor-pointer',
                                item === page
                                    ? 'border border-cust-primary rounded-full shadow-[0_1px_6px_rgba(0,0,0,0.06)]'
                                    : '',
                            )}
                        >
                            {item}
                        </button>
                    ),
                )}

                <button
                    onClick={scrollToTop}
                    aria-label="Scroll to top"
                    className={cn(
                        'ml-[8px] w-[30px] h-[30px] rounded-full border border-neutral-300 shadow-[0_1px_6px_rgba(0,0,0,0.06)]',
                        'flex items-center justify-center',
                        'hover:border-black hover:text-black transition-colors duration-150 cursor-pointer',
                        'text-neutral-500',
                    )}
                >
                    <ScrollToTopIcon />
                </button>
            </div>
        </div>
    );
}
